import React, {forwardRef, useContext} from 'react';
import PropTypes from 'prop-types';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {LocationContext} from '~/components/LocationInfo';

export const routerPropTypes = PropTypes.shape({
  location: PropTypes.oneOfType([PropTypes.object, PropTypes.func]), // The func type is passed only on automated tests
  navigate: PropTypes.func,
  params: PropTypes.object,
  searchParams: PropTypes.object
});

const propTypes = {
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({current: PropTypes.any})
  ])
};

/**
 * see https://reactrouter.com/docs/en/v6/faq#what-happened-to-withrouter-i-need-it
 *
 * @param {React.ComponentType} Component - The component to be wrapped.
 */
function withRouter(Component) {
  /** @param {PropTypes.InferProps<typeof propTypes>} props - Component properties. */
  function ComponentWithRouterProp(props) {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    const locationContext = useContext(LocationContext);
    const lastPath = locationContext?.locationInfo?.prevLocation?.pathname;
    const {forwardedRef, ...rest} = props;

    return (
      <Component
        ref={forwardedRef}
        {...rest}
        router={{lastPath, location, navigate, params}}
      />
    );
  }
  ComponentWithRouterProp.displayName = `withRouter(${Component.displayName})`;
  ComponentWithRouterProp.propTypes = propTypes;
  // forwardRef to allow receiving a ref and passing it to the wrapped component -> https://es.reactjs.org/docs/forwarding-refs.html
  const ComponentWithRouterAndRef = forwardRef((props, ref) => (
    <ComponentWithRouterProp {...props} forwardedRef={ref} />
  ));
  ComponentWithRouterAndRef.displayName = `withRouter(${Component.displayName})`;

  return ComponentWithRouterAndRef;
}

export default withRouter;
